import React from 'react';
import styled from 'styled-components';
import { FaLock, FaRobot, FaBrain, FaDesktop, FaUserFriends, FaMagic, FaLanguage, FaWifi, FaUsers, FaShieldAlt, FaMobile } from 'react-icons/fa';

const FeaturesSection = styled.section`
  padding: 6rem 0;
  background-color: var(--bg-light);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(to right, transparent, var(--accent-color), transparent);
    opacity: 0.2;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 700px;
  margin: 0 auto 4rem;
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 800;
  color: var(--text-dark);
  margin-bottom: 1rem;
  
  span {
    color: var(--primary-color);
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  font-size: 1.125rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background-color: var(--white);
  border-radius: var(--border-radius);
  padding: 2rem;
  box-shadow: var(--box-shadow);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  border: 1px solid var(--gray-200);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(var(--primary-rgb), 0.1);
  }
`;

const FeatureIcon = styled.div`
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border-radius: 16px;
  margin-bottom: 1.5rem;
  color: var(--white);
  font-size: 1.75rem;
`;

const FeatureTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: var(--text-dark);
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: var(--text-medium);
  line-height: 1.6;
`;

const Features = () => {
  const features = [
    {
      icon: <FaLanguage />,
      title: "Multi-Language Support",
      description: "Communicate naturally in major Indian languages including Hindi, Bengali, Tamil, Telugu, Marathi, and more. Our AI understands and responds fluently in your preferred language.",
      color: "#8A2BE2"
    },
    {
      icon: <FaWifi />,
      title: "Flexible Connectivity",
      description: "Choose between online mode for enhanced capabilities or offline mode for privacy and limited connectivity areas. Your AI assistant works seamlessly in both modes.",
      color: "#9370DB"
    },
    {
      icon: <FaUsers />,
      title: "Specialized Agents",
      description: "Purpose-built AI agents for different needs - helping farmers with crop advice, supporting rural businesses with growth strategies, and assisting students with studies.",
      color: "#7B68EE"
    },
    {
      icon: <FaShieldAlt />,
      title: "Privacy First",
      description: "End-to-end encryption and local processing ensure your conversations stay private. Choose what data to share and what to keep local.",
      color: "#6A5ACD"
    },
    {
      icon: <FaBrain />,
      title: "Contextual Understanding",
      description: "Our AI understands local contexts, cultural nuances, and domain-specific knowledge to provide relevant and accurate assistance.",
      color: "#483D8B"
    },
    {
      icon: <FaMobile />,
      title: "Lightweight Design",
      description: "Optimized for all Android devices, works smoothly even on basic smartphones with minimal storage and processing power.",
      color: "#4B0082"
    }
  ];

  return (
    <FeaturesSection id="features">
      <Container>
        <SectionHeader>
          <SectionTitle>
            Powerful <span>Features</span>
          </SectionTitle>
          <SectionSubtitle>
            Discover how Hallow makes AI accessible and useful for everyone, 
            from farmers to students, in their preferred language and connectivity mode.
          </SectionSubtitle>
        </SectionHeader>
        
        <FeaturesGrid>
          {features.map((feature, index) => (
            <FeatureCard key={index}>
              <FeatureIcon style={{ background: feature.color }}>
                {feature.icon}
              </FeatureIcon>
              <FeatureTitle>{feature.title}</FeatureTitle>
              <FeatureDescription>{feature.description}</FeatureDescription>
            </FeatureCard>
          ))}
        </FeaturesGrid>
      </Container>
    </FeaturesSection>
  );
};

export default Features; 